import { css } from '@emotion/react';
import { define } from '@owenscorning/pcb.alpha';

import { useCallback, useEffect, useRef, useState } from 'react';

// The Library imports every file under it, so a static ace import here
// ships the editor to every visitor. This component only has an edit branch.
const JsonEditor = ({ path, value, onChange, readOnly }) => {
  const ref = useRef();
  const [ace, setAce] = useState(null);

  useEffect(() => {
    let live = true;
    // Sequential, not Promise.all: the resolver and the extension both open on
    // a bare global `ace` that only the core sets, and parallel imports
    // evaluate in completion order.
    (async () => {
      await import('ace-builds/src-noconflict/ace');
      const reactAce = await import('react-ace');
      await import('ace-builds/webpack-resolver');
      const beautify = await import('ace-builds/src-noconflict/ext-beautify');
      if (live) {
        setAce({ AceEditor: reactAce.default, Beautify: beautify.default });
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  useEffect(
    () =>
      ace && ref.current
        ? ace.Beautify.beautify(ref.current.editor.session)
        : null,
    [ace]
  );

  const [error, setError] = useState();
  const localOnChange = useCallback(
    v => {
      try {
        onChange ? onChange(JSON.parse(v)) : null;
        setError(null);
      } catch (e) {
        setError(e.message);
      }
    },
    [onChange]
  );

  if (!ace) return null;
  const { AceEditor, Beautify } = ace;

  return (
    <>
      <div
        css={css`
          width: 100%;
          height: 100%;
          min-height: 400px;
          display: flex;
          gap: 16px;
          & > div {
            width: 100%;
            overflow-y: auto;
          }
        `}
      >
        <div>
          <AceEditor
            ref={ref}
            mode="json"
            theme="monokai"
            readOnly={readOnly}
            defaultValue={value ? JSON.stringify(value, null, 2) : value}
            onChange={localOnChange}
            name={`repl${path}`}
            tabSize={2}
            fontSize={16}
            width="100%"
            height="100%"
            wrapEnabled
            showPrintMargin={false}
            commands={Beautify.commands}
            editorProps={{ $blockScrolling: true }}
            setOptions={{
              enableBasicAutocompletion: false,
              enableLiveAutocompletion: false,
              enableSnippets: false,
            }}
          />
        </div>
      </div>
      {error && (
        <UI.Paragraph red>
          Failed to parse!
          <br />
          <pre>Error: {error}</pre>
        </UI.Paragraph>
      )}
    </>
  );
};

export default define`Json`('0.0.1')({
  edit: [
    ({ path, value, onChange, readOnly }) => (
      <JsonEditor
        path={path}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    ),
  ],
});
